import type React from "react";
import { useCallback } from "react";

import { assignRef } from "@/util/helpers.mjs";

// https://github.com/facebook/react/issues/15176
// https://github.com/facebook/react/issues/21903
// https://github.com/reactjs/rfcs/pull/205
// useCallbackRef combines aspects from the following hooks:
// - useCallback: memoizes the callback on `deps` arg. additionally you may call
// the function dierctly as ref(node, ..args) if needed
// - useRef: stores a ref to the dom element in ref.current
// - useEffect: cleans up the callback when the ref is nulled
// - forwardRef: optional ref chaining (eg. forwardRef) via `passthroughRef` arg
export default function useCallbackRef<
  // this infers the input arguments of the callback function
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Fn extends (current: any, ...args: any[]) => void | (() => void),
  T = Arguments<Fn> extends [infer R, ...unknown[]] ? R : never,
  Rest extends unknown[] = Arguments<Fn> extends [unknown, ...infer Rest]
    ? Rest
    : never,
>(
  rawCallback: Fn,
  deps: React.DependencyList = [],
  passthroughRef?: React.MutableRefObject<T> | ((node: T) => void),
): React.MutableRefObject<T> & Fn {
  const callbackRef = useCallback((current: T, ...args: Rest) => {
    if (callbackRef.cleanup) {
      callbackRef.cleanup();
      Object.assign(callbackRef, { cleanup: null });
    }

    if (current) {
      const cleanup = rawCallback(current, ...args);
      Object.assign(callbackRef, { cleanup });
    }

    // assign this after the callback, current value is given by arg0 and this way it's
    // technically possible to get the previous value (callbackRef.current)
    Object.assign(callbackRef, { current });
    if (passthroughRef) assignRef(passthroughRef, current);
  }, deps) as Fn &
    React.MutableRefObject<T> & {
      cleanup?: () => void;
    };

  return callbackRef;
}
